// The Sentinel frame: the static demo's sidebar and top bar
// (giothub-coldchain-ui-static src/components/Shell.jsx), built on the
// shell's frame hooks (menu, session, language, light/dark), so permissions,
// sign-out and the saved language come from the shell.
import { useEffect, useState } from "preact/hooks";
import { Link } from "wouter-preact";
import { isActive, useAlarmsBadge, useI18n, useMenu, useRoute, useSession, useTheme, type FrameProps, type MenuItem } from "@gimasi/giothub-shell";
import Icon from "../components/Icon.jsx";
import { FutureFridgesMark, GiothubMark } from "../components/Logo.jsx";
import { useT } from "../i18n";
import { navMode, setNavMode, wide } from "../lib/navmode";
import { accounts, bound, accountsReady, current, currentAccount, multi, setAccount, tree } from "../lib/account";
import { go } from "../lib/nav";

const LANG_LABEL: Record<string, string> = { en: "EN", de: "DE", it: "IT", fr: "FR" };

function Item({ i, at, badge }: { i: MenuItem; at: string; badge?: number }) {
  const on = isActive(i.href, at);
  return (
    <Link href={i.href} class={on ? "on" : ""} aria-current={on ? "page" : undefined}>
      <Icon name={i.icon || "dash"} />
      {i.label}
      {badge ? <span class="badge">{badge}</span> : null}
    </Link>
  );
}

const PARTNER_ONLY = ["/customers", "/partner"];

function AccountSwitch() {
  const t = useT();
  const [open, setOpen] = useState(false);
  const cur = current.value;
  const b = bound();
  if (!multi.value) {
    const only = accounts.value[0];
    return <div class="tenant"><small>{t("app.customer")}</small><b>{b?.name || only?.name || ""}</b></div>;
  }
  return (
    <div style="position:relative">
      <button class="tenant" type="button" aria-expanded={open} onClick={() => setOpen(!open)} title={t("nav.switchCustomer")}>
        <small>{cur ? t("x.kind." + cur.kind) : t("x.account")}</small>
        <b>{cur ? cur.name : b ? b.name : t("x.allAccounts")}</b>
      </button>
      {open && (
        <div class="menu" style="left:0;right:0" onMouseLeave={() => setOpen(false)}>
          <button type="button" onClick={() => { setAccount(""); setOpen(false); }}>{b ? b.name : t("x.allAccounts")}</button>
          {tree.value.filter((r) => r.a.parent_id).map(({ a, depth }) => (
            <button type="button" key={a.id} style={`padding-left:${10 + (depth - 1) * 16}px`} onClick={() => { setAccount(a.id); setOpen(false); }}>
              {a.name} <small style="color:var(--muted)">{t("x.kind." + a.kind)}</small>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export function SentinelFrame({ children }: FrameProps) {
  const t = useT();
  const { path } = useRoute();
  const { user, signOut } = useSession();
  const { lang, languages, setLang } = useI18n();
  const { mode, setMode } = useTheme();
  const alarms = useAlarmsBadge();
  // the shell's own modules also offer /devices: Sentinel's page wins the
  // route, so its second menu entry is dropped (first href wins)
  const seen = new Set<string>();
  const menu = useMenu()
    .map((e) => (e.kind === "item" ? e : { ...e, items: e.items.filter((i) => !seen.has(i.href) && seen.add(i.href)) }))
    .filter((e) => (e.kind === "item" ? !seen.has(e.href) && !!seen.add(e.href) : e.items.length > 0))
    // a customer address shows one customer: no partner pages
    .map((e) => (e.kind === "item" || !bound() ? e : { ...e, items: e.items.filter((i) => !PARTNER_ONLY.some((p) => i.href.startsWith(p))) }))
    .filter((e) => !(e.kind === "item" && bound() && PARTNER_ONLY.some((p) => e.href.startsWith(p))) && (e.kind === "item" || e.items.length > 0));
  useEffect(() => { if (bound() && PARTNER_ONLY.some((p) => path.startsWith(p))) go("/"); }, [path]);
  const [userMenu, setUserMenu] = useState(false);
  useEffect(() => { accountsReady(); }, []);
  useEffect(() => { document.documentElement.setAttribute("data-theme", mode === "dark" ? "dark" : "light"); }, [mode]);
  void currentAccount.value; // re-render on account change
  const initials = (user.name || user.email || "?").split(/[\s@.]+/).filter(Boolean).slice(0, 2).map((s) => s[0]!.toUpperCase()).join("");
  return (
    <div class="shell" data-frame="sentinel">
      <nav class="side" aria-label={t("nav.main")}>
        <Link class="brand" href="/">
          <FutureFridgesMark size={38} />
          <div><div class="brand-word">FutureFridges</div><div class="brand-sub">Sentinel · {t("app.edition")}</div></div>
        </Link>
        <AccountSwitch />
        {menu.map((e) => e.kind === "item"
          ? <div class="nav" key={e.href}><Item i={e} at={path} /></div>
          : (
            <div class="nav" key={e.label}>
              <div class="nav-h">{e.label}</div>
              {e.items.map((i) => <Item key={i.href} i={i} at={path} badge={i.href === "/alarms" && alarms.allowed ? alarms.open : 0} />)}
            </div>
          ))}
        <div class="side-foot">
          <span class="powered" style="display:flex;gap:8px;align-items:center"><GiothubMark size={16} /> {t("app.powered")} <b>gIoTHUB</b></span>
          <span>{location.host}</span>
          <span class="mono" title={`${t("x.build")} ${new Date(__BUILD__.date).toLocaleString(t.locale)}`} data-testid="build">{__BUILD__.tag || "v" + __BUILD__.version} · {__BUILD__.commit || "dev"} · {new Date(__BUILD__.date).toLocaleDateString(t.locale, { day: "2-digit", month: "2-digit", year: "numeric" })}</span>
        </div>
      </nav>
      <main class="main" id="main">
        <header class="top">
          <span style="flex:1" />
          {path.startsWith("/loc") && (
            <div class="seg" role="group" aria-label={t("top.navView")}>
              <button type="button" class={(navMode.value.monitor || "path") === "tree" && wide() ? "on" : ""} disabled={!wide()} title={wide() ? "" : t("top.treeWide")} onClick={() => setNavMode("monitor", "tree")}><Icon name="tree" size={16} />{t("top.tree")}</button>
              <button type="button" class={(navMode.value.monitor || "path") === "path" || !wide() ? "on" : ""} onClick={() => setNavMode("monitor", "path")}><Icon name="path" size={16} />{t("top.path")}</button>
            </div>
          )}
          <label class="sr" for="langSel">{t("top.language")}</label>
          <select id="langSel" class="lang" value={lang} onChange={(e) => setLang((e.target as HTMLSelectElement).value)}>
            {languages.map((l) => <option value={l} key={l}>{LANG_LABEL[l] || l.toUpperCase()}</option>)}
          </select>
          <button class="iconbtn" type="button" aria-label={t(mode === "dark" ? "top.light" : "top.dark")} onClick={() => setMode(mode === "dark" ? "light" : "dark")}>
            <Icon name={mode === "dark" ? "sun" : "moon"} />
          </button>
          <div style="position:relative">
            <button class="avatar" type="button" aria-label={user.name} onClick={() => setUserMenu(!userMenu)}>{initials}</button>
            {userMenu && (
              <div class="menu" style="left:auto;right:0;min-width:260px" onMouseLeave={() => setUserMenu(false)}>
                <div style="padding:8px 10px"><b>{user.name}</b><div style="color:var(--muted);font-size:13px">{user.email}</div></div>
                <button type="button" onClick={signOut}><Icon name="logout" size={16} />{t("x.signOut")}</button>
              </div>
            )}
          </div>
        </header>
        {path.startsWith("/settings/api") ? <div class="page">{children}</div> : children /* shell module pages bring no page padding */}
      </main>
    </div>
  );
}
